import type { ResultOf } from '@graphql-typed-document-node/core';
import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import EditFinancialAccountDialog from 'src/components/financial-account/edit-financial-account-dialog';
import TrackedButton from 'src/components/tracked/tracked-button';
import { graphql, unmaskFragment } from 'src/gql';
import { accountDeleteButtonText, isLinkedAccount } from 'src/utils/account';

import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';

import { ViewPortfolioItemDialog } from '../views/view-portfolio-item-dialog';
import PortfolioAccountDescriptionRow from './portfolio-account-description-row';
import RemoveAccountDialog from './remove-account-dialog';
import { ViewAccountDialogDetailRows } from './view-account-dialog-detail-rows';

export const ViewAccountDialogFragment = graphql(/* GraphQL */ `
  fragment ViewAccountDialog on Account {
    ...AccountDeleteButtonText
    ...EditFinancialAccountDialog
    ...IsLinkedAccount
    ...RemoveAccountDialog
    ...PortfolioAccountDescriptionRow
    ...ViewAccountDialogDetailRows
    providerAccount {
      source
    }
    subtype
    type
  }
`);

export function ViewAccountDialog({
  accountFrag,
  onEdit,
  onClose,
}: {
  readonly accountFrag: FragmentType<typeof ViewAccountDialogFragment>;
  readonly onEdit: Deprecated_HandleItemUpdate;
  readonly onClose: () => void;
}) {
  const account = unmaskFragment(ViewAccountDialogFragment, accountFrag);
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const isSmallScreen = useIsSmallScreen();

  if (isEditing) {
    return (
      <EditFinancialAccountDialog
        accountFrag={account}
        onEdit={onEdit}
        onBack={() => {
          setIsEditing(false);
        }}
        onClose={() => {
          setIsEditing(false);
          // TODO: migrate linked account case
          onClose();
        }}
      />
    );
  }

  if (isRemoving) {
    return (
      <RemoveAccountDialog
        accountFrag={account}
        onClose={() => {
          setIsRemoving(false);
        }}
        onRemove={onEdit}
      />
    );
  }

  return (
    <ViewPortfolioItemDialog
      deleteButton={
        <DeleteButton
          account={account}
          onClick={() => {
            setIsRemoving(true);
          }}
          isSmallScreen={isSmallScreen}
        />
      }
      descriptionRow={<PortfolioAccountDescriptionRow accountFrag={account} />}
      detailRows={<ViewAccountDialogDetailRows accountFrag={account} onEdit={onEdit} />}
      editButton={
        <EditButton
          account={account}
          onClick={() => {
            setIsEditing(true);
          }}
          isSmallScreen={isSmallScreen}
        />
      }
      onClose={onClose}
    />
  );
}

type DeleteButtonProps = {
  readonly account: ResultOf<typeof ViewAccountDialogFragment>;
  readonly onClick: () => void;
  readonly isSmallScreen: boolean;
};
function DeleteButton({ account, onClick, isSmallScreen }: DeleteButtonProps) {
  if (account.providerAccount?.source === 'ORION' || account.subtype === 'pledged_asset_line') {
    return null;
  }
  return (
    <TrackedButton
      elementName="view-account-dialog-delete-button"
      size={isSmallScreen ? 'sm' : 'default'}
      variant="secondary"
      onClick={onClick}
      className={isSmallScreen ? 'w-[156px]' : undefined}
    >
      {accountDeleteButtonText(account)}
    </TrackedButton>
  );
}

type EditButtonProps = {
  readonly account: ResultOf<typeof ViewAccountDialogFragment>;
  readonly onClick: () => void;
  readonly isSmallScreen: boolean;
};
function EditButton({ account, onClick, isSmallScreen }: EditButtonProps) {
  if (isLinkedAccount(account) || account.subtype === 'pledged_asset_line') {
    return null;
  }
  return (
    <TrackedButton
      elementName="view-account-dialog-edit-button"
      size={isSmallScreen ? 'sm' : 'default'}
      variant="secondary"
      onClick={onClick}
      className={isSmallScreen ? 'w-[156px]' : undefined}
    >
      Edit details
    </TrackedButton>
  );
}
